// ONE templated page for the whole store catalog. Every report shares the
// query grammar (preset | from+to, compare, groupBy, format) and the result
// shape — typed columns, rows keyed by column, a totals row, the compare
// period — so the page renders whatever columns the server describes. The
// query lives in the URL, so a report view is a shareable link.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { Download } from 'lucide-react';
import { api, label, type Report } from '../../lib/api';
import { useBase, useScope } from '../../scope';
import { PRESETS, SETTLEMENT_PROVIDERS, reportBySlug, slugOf } from './registry';
import { ReportTable } from './table';

type Query = { from: string; to: string; preset: string; groupBy: string; compare: boolean };

const queryOf = (p: URLSearchParams): Query => ({
  from: p.get('from') ?? '',
  to: p.get('to') ?? '',
  preset: p.get('preset') ?? '',
  groupBy: p.get('groupBy') ?? '',
  compare: p.get('compare') === 'true',
});

export function ReportView() {
  const { report: slug } = useParams();
  const { rid, sid, store } = useScope();
  const base = useBase();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const seq = useRef(0);
  const found = reportBySlug(slug);
  const root = `/r/${rid}/s/${sid}/reports`;

  // The query is component state (controls answer at once) mirrored into
  // the URL (a report view is a shareable link).
  const [q, setQ] = useState<Query>(() => queryOf(params));
  const { from, to, groupBy, compare } = q;
  const preset = from || to ? '' : q.preset || 'today';
  const query = { from: from || undefined, to: to || undefined, preset: preset || undefined, groupBy: groupBy || undefined, compare: compare || undefined };
  const apiPath = found ? `${base}/reports/${found.path}` : '';
  const queryKey = JSON.stringify(query);

  const load = useCallback(async () => {
    if (!apiPath) return;
    const mine = ++seq.current;
    setLoading(true);
    try {
      const r = await api.get<Report>(apiPath, JSON.parse(queryKey));
      if (mine !== seq.current) return;
      setData(r);
      setError(null);
    } catch (e) {
      if (mine === seq.current) setError(e instanceof Error ? e.message : 'Failed to load.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [apiPath, queryKey]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const next = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v !== undefined) next.set(k, String(v));
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [queryKey]);

  const patch = (p: Partial<Query>) => setQ((cur) => ({ ...cur, ...p }));

  async function download(format: 'csv' | 'xlsx') {
    setDownloading(format);
    try {
      const blob = await api.get<Blob>(apiPath, { ...query, format });
      const range = data?.range;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${slugOf(found!.path)}${range ? `-${range.from}-${range.to}` : ''}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download failed.');
    } finally {
      setDownloading(null);
    }
  }

  if (!found) {
    return (
      <>
        <div className="page-head"><div><div className="overline"><Link to={root}>Reports</Link></div><h1>Unknown report</h1></div></div>
        <div className="err" role="alert">No report is called “{slug}”. <Link to={root}>Back to the catalog.</Link></div>
      </>
    );
  }
  const { def, path } = found;
  const tz = data?.timezone ?? store?.timezone;
  const provider = path.startsWith('settlements/') ? path.split('/')[1] : null;

  return (
    <>
      <div className="page-head" style={{ alignItems: 'center' }}>
        <div>
          <div className="overline"><Link to={root}>Reports</Link> · {def.group}</div>
          <h1>{def.title}{provider ? ` · ${label(provider)}` : ''}</h1>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" disabled={!data || downloading !== null} onClick={() => void download('csv')}>
            <Download size={16} /> {downloading === 'csv' ? 'Preparing…' : 'Download CSV'}
          </button>
          <button className="btn btn-outline" disabled={!data || downloading !== null} onClick={() => void download('xlsx')}>
            <Download size={16} /> {downloading === 'xlsx' ? 'Preparing…' : 'Download XLSX'}
          </button>
        </div>
      </div>
      {error && <div className="err" role="alert">{error}</div>}

      {provider && (
        <div className="filters" style={{ marginBottom: 10 }} aria-label="Provider">
          {SETTLEMENT_PROVIDERS.map((p) => (
            <Link key={p} className={'filter' + (p === provider ? ' on' : '')} to={`${root}/settlements-${p}?${params.toString()}`} replace>
              {label(p)}
            </Link>
          ))}
        </div>
      )}

      <div className="filters" style={{ marginBottom: 10 }}>
        {PRESETS.map((p) => (
          <button key={p.key} className={'filter' + (preset === p.key ? ' on' : '')} onClick={() => patch({ preset: p.key, from: '', to: '' })}>
            {p.label}
          </button>
        ))}
      </div>
      <div className="filters" style={{ marginBottom: 18, alignItems: 'center' }}>
        <input className="input" type="date" aria-label="From" value={from || data?.range.from || ''} style={{ maxWidth: 170 }}
          onChange={(e) => patch({ from: e.target.value, to: to || e.target.value, preset: '' })} />
        <span style={{ color: 'var(--ink-3)' }}>to</span>
        <input className="input" type="date" aria-label="To" value={to || data?.range.to || ''} style={{ maxWidth: 170 }}
          onChange={(e) => patch({ to: e.target.value, from: from || e.target.value, preset: '' })} />
        {def.groupBy && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <span style={{ color: 'var(--ink-3)' }}>Group by</span>
            <select className="input" aria-label="Group by" style={{ width: 'auto' }} value={groupBy || def.groupBy[0]}
              onChange={(e) => patch({ groupBy: e.target.value === def.groupBy![0] ? '' : e.target.value })}>
              {def.groupBy.map((g) => <option key={g} value={g}>{label(g)}</option>)}
            </select>
          </label>
        )}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" aria-label="Compare with the previous period" checked={compare} onChange={(e) => patch({ compare: e.target.checked })} />
          Compare with the previous period
        </label>
      </div>

      {!data && !error && <div className="loading">Loading…</div>}
      {data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, opacity: loading ? 0.6 : 1 }} data-report={data.report} data-group-by={data.groupBy ?? ''}>
          <div style={{ color: 'var(--ink-3)', fontSize: 13 }} data-range={`${data.range.from}..${data.range.to}`}>
            {data.range.from === data.range.to ? data.range.from : `${data.range.from} – ${data.range.to}`} · {data.timezone}
            {data.groupBy ? ` · by ${label(data.groupBy)}` : ''}
          </div>

          {data.summary && Object.keys(data.summary).length > 0 && (
            <div className="stats" data-section="summary">
              {Object.entries(data.summary).map(([k, v]) => (
                <div key={k} className="stat">
                  <div className="overline">{label(k)}</div>
                  <div className="v" data-stat={k}>{v ?? '—'}</div>
                </div>
              ))}
            </div>
          )}

          <ReportTable columns={data.columns} rows={data.rows} totals={data.totals} tz={tz} section="report" />

          {data.compare && (
            <div className="panel" data-section="compare-period" data-range={`${data.compare.range.from}..${data.compare.range.to}`}>
              <h2>
                Previous period · {data.compare.range.from === data.compare.range.to ? data.compare.range.from : `${data.compare.range.from} – ${data.compare.range.to}`}
              </h2>
              <ReportTable columns={data.compare.table.columns} rows={data.compare.table.rows} totals={data.compare.table.totals} tz={tz} section="compare" />
            </div>
          )}
        </div>
      )}
    </>
  );
}
